<template>
  <div class="h-full">
    <!-- Loading State -->
    <img
      v-if="state.isLoading"
      class="mx-auto"
      src="https://res.cloudinary.com/iubh/image/upload/v1613575530/07%20-%20Pages/Unit%20Bilder/Duales%20Studium/Bewerberportal/loading-bewerberportal-paper-p-unscreen_oa0wvh.gif"
      alt=""
    />

    <!-- Error State -->
    <div v-else-if="state.lastError" class="text-center py-8">
      <div class="text-lg text-red-600">{{ state.lastError }}</div>
      <button
        @click="loadFlow"
        class="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        Neu laden
      </button>
    </div>

    <!-- Flow Content -->
    <div v-else class="h-full">
      <component
        v-if="currentComponent"
        :is="currentComponent"
        :current-step-id="currentStep?.id"
        :total-steps="flowSteps.length"
        :current-step-index="currentStepIndex"
        @request-next="onRequestNext"
        @request-back="(steps) => previousStep(steps)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type Component,
  computed,
  onMounted,
  provide,
  ref,
  shallowRef,
} from 'vue'
import stepResolver from '@/modules/wizard/services/step-resolver.service'
import wizardFlowService from '@/modules/wizard/services/wizard-flow.service'
import { WizardPreconditions } from '@/modules/wizard/types/WizardPreconditions'
import { WizardFlowStepEvents } from '@/modules/wizard/types/WizardFlowStep'
import accountStore from '@/stores/account.store'
import { UniversityEntranceQualificationType } from '@/enums'
import eebPlacement from '@/services/amplitude/eeb-placement.service'
import amplitudeExperimentService from '@/services/amplitude/amplitude-experiment.service'

/** Preconditions plus derived keys used in flow JSON `if` / `unless` */
type StepEvalContext = WizardPreconditions & {
  hasAbroadDiploma?: boolean
  eebInOnboarding?: boolean
}

const props = defineProps<{
  flowName: string
  context?: WizardPreconditions
}>()

const emit = defineEmits<WizardFlowStepEvents>()

const state = ref({
  isLoading: true,
  lastError: null as string | null,
})

const componentRegistry = shallowRef<Record<string, Component>>({})

const currentStepIndex = ref(0)
const flowSteps = ref<Array<{ id: string }>>([])
const currentStep = computed(() => flowSteps.value[currentStepIndex.value])

const stepEvalContext = computed((): StepEvalContext | undefined => {
  if (!props.context) return undefined

  return {
    ...props.context,
    hasAbroadDiploma:
      props.context.hasUniversityEntranceQualificationFromAbroad ??
      accountStore.state.account?.universityEntranceQualification ===
        UniversityEntranceQualificationType.UNIVERSITY_ENTRANCE_DIPLOMA_FROM_ABROAD,
    // EEB Placement Experiment (OCPT-860): variant B removes the EEB booking
    // step from onboarding (EEB is shown as a mainscreen task instead).
    eebInOnboarding: !eebPlacement.isMainscreenVariant(),
  }
})

/**
 * Evaluates **all** `if` / `unless` keys from the flow JSON against the same
 * context as the UI (incl. derived fields like `hasAbroadDiploma`). Used for
 * every conditional step in every flow (OnboardingFlow, MatchingQuizFlow, …).
 */
const shouldRenderStep = (
  step: { id: string; if?: string[]; unless?: string[] },
  context: StepEvalContext | undefined,
): boolean => {
  if (!context) return true

  const ctx = context as Record<string, unknown>
  let ifResult = true
  let unlessResult = true

  if (step.if) {
    ifResult = step.if.every((condition) => {
      const value = ctx[condition]
      return typeof value === 'boolean' && value === true
    })
  }

  if (step.unless) {
    unlessResult = step.unless.some((condition) => {
      const value = ctx[condition]
      return typeof value === 'boolean' && value === false
    })
  }

  return ifResult && unlessResult
}

/**
 * Returns the step’s component loader if conditions pass and the id is registered;
 * otherwise `null`. Single `resolve` per check — use this for UI + recovery + next/prev.
 */
function tryResolveStepLoader(
  step: { id: string; if?: string[]; unless?: string[] },
  ctx: StepEvalContext | undefined,
): (() => Promise<{ default: Component }>) | null {
  if (!shouldRenderStep(step, ctx)) return null
  try {
    return stepResolver.resolve(step.id)
  } catch (err) {
    if (import.meta.env.DEV) {
      console.debug(`[WizardFlow] Step "${step.id}" not resolvable:`, err)
    }
    return null
  }
}

function isStepRenderableForUi(
  step: { id: string; if?: string[]; unless?: string[] },
  ctx: StepEvalContext | undefined,
): boolean {
  return tryResolveStepLoader(step, ctx) !== null
}

// Get the next available step based on conditions
const getNextAvailableStep = (
  currentIndex: number,
  context: StepEvalContext | undefined,
): number => {
  for (let i = currentIndex + 1; i < flowSteps.value.length; i++) {
    if (isStepRenderableForUi(flowSteps.value[i], context)) {
      return i
    }
  }
  return -1 // No more available steps
}

// Get the previous available step based on conditions
const getPreviousAvailableStep = (
  currentIndex: number,
  context: StepEvalContext | undefined,
): number => {
  for (let i = currentIndex - 1; i >= 0; i--) {
    if (isStepRenderableForUi(flowSteps.value[i], context)) {
      return i
    }
  }
  return -1 // No previous available steps
}

const currentComponent = computed(() => {
  if (!currentStep.value) return null

  const loader = tryResolveStepLoader(currentStep.value, stepEvalContext.value)
  if (!loader) return null

  const componentPath = loader.toString()
  return componentRegistry.value[componentPath] || null
})

// Called when a step requests auto-advance (after its internal logic)
const onRequestNext = () => nextStep()

const loadComponent = async (
  componentLoader: () => Promise<{ default: Component }>,
) => {
  const componentPath = componentLoader.toString()
  if (componentRegistry.value[componentPath]) {
    return componentRegistry.value[componentPath]
  }

  try {
    const component = await componentLoader()
    componentRegistry.value[componentPath] = component.default
    return component.default
  } catch (err) {
    console.error(`Failed to load component: ${componentPath}`, err)
    return null
  }
}

const loadFlow = async () => {
  try {
    state.value.isLoading = true
    state.value.lastError = null

    // Wait until Experiment variants are settled before evaluating conditional
    // steps (e.g. `eebInOnboarding`). The Experiment fetch runs behind the
    // consent flow and would otherwise return after the step list is built,
    // making a variant-B user still see the EEB onboarding step. Bounded by an
    // internal timeout that falls back to control.
    await amplitudeExperimentService.whenReady()

    const flowModule = await import(
      `@/modules/wizard/flows/${props.flowName}.json`
    )
    const flowData = flowModule.default

    if (!flowData.steps || !Array.isArray(flowData.steps)) {
      throw new Error('Invalid flow format: missing or invalid steps array')
    }

    flowSteps.value = flowData.steps

    // Load saved progress and resume from last position
    const savedProgress = wizardFlowService.getProgress(props.flowName)
    if (
      savedProgress !== null &&
      savedProgress >= 0 &&
      savedProgress < flowData.steps.length
    ) {
      currentStepIndex.value = savedProgress
    } else {
      currentStepIndex.value = 0
    }

    // Save initial step progress only if we're starting from the beginning
    if (
      currentStepIndex.value === 0 &&
      flowData.steps.length > 0 &&
      props.flowName
    ) {
      wizardFlowService.saveProgress(props.flowName, 0)
    }

    // Preload all components for the flow
    for (const step of flowData.steps) {
      try {
        const componentLoader = stepResolver.resolve(step.id)
        await loadComponent(componentLoader)
      } catch (err) {
        console.error(`Failed to preload component for step ${step.id}:`, err)
      }
    }

    // Recover if saved progress points to a step that's no longer renderable
    reconcileSavedStepIfStuck()
  } catch (err) {
    console.error('Error loading flow:', err)
    state.value.lastError = `Failed to load flow: ${props.flowName}`
  } finally {
    state.value.isLoading = false
  }
}

/**
 * If saved index points at a step that fails {@link isStepRenderableForUi} (any
 * `if`/`unless` in the JSON, any flow), move to the nearest valid step — first
 * backward, then forward. Same visibility rules as the visible wizard.
 */
const reconcileSavedStepIfStuck = () => {
  const steps = flowSteps.value
  if (!steps.length) return

  const ctx = stepEvalContext.value

  const indexIsRenderable = (i: number): boolean => {
    const step = steps[i]
    if (!step) return false
    return isStepRenderableForUi(step, ctx)
  }

  const saved = currentStepIndex.value
  if (indexIsRenderable(saved)) return

  for (let i = saved - 1; i >= 0; i--) {
    if (indexIsRenderable(i)) {
      currentStepIndex.value = i
      wizardFlowService.saveProgress(props.flowName, i)
      console.warn(
        `[WizardFlow] Saved step ${saved} not renderable; moved back to ${i}`,
      )
      return
    }
  }
  for (let i = saved + 1; i < steps.length; i++) {
    if (indexIsRenderable(i)) {
      currentStepIndex.value = i
      wizardFlowService.saveProgress(props.flowName, i)
      console.warn(
        `[WizardFlow] Saved step ${saved} not renderable; no earlier step worked, advanced to ${i}`,
      )
      return
    }
  }

  console.error('[WizardFlow] No renderable step for flow', props.flowName)
  state.value.lastError =
    'Dieser Schritt konnte nicht geladen werden. Bitte lade die Seite neu.'
}

const nextStep = () => {
  const nextIndex = getNextAvailableStep(
    currentStepIndex.value,
    stepEvalContext.value,
  )
  if (nextIndex !== -1) {
    currentStepIndex.value = nextIndex
    wizardFlowService.saveProgress(props.flowName, nextIndex)
  } else {
    // No more steps available, flow is complete
    emit('flow-complete')
  }
}

const previousStep = (steps: number = 1) => {
  let targetIndex = currentStepIndex.value
  let stepsRemaining = steps

  // Go back the specified number of steps
  while (stepsRemaining > 0 && targetIndex > 0) {
    const prevIndex = getPreviousAvailableStep(
      targetIndex,
      stepEvalContext.value,
    )
    if (prevIndex === -1) {
      break
    }
    targetIndex = prevIndex
    stepsRemaining--
  }

  if (targetIndex !== currentStepIndex.value) {
    currentStepIndex.value = targetIndex
    wizardFlowService.saveProgress(props.flowName, targetIndex)
  }
}

const renderableSteps = computed(() =>
  flowSteps.value.filter((step) =>
    isStepRenderableForUi(step, stepEvalContext.value),
  ),
)

const currentRenderableIndex = computed(() =>
  renderableSteps.value.findIndex((step) => step.id === currentStep.value?.id),
)

provide('wizardContext', {
  currentStepId: computed(() => currentStep.value?.id),
  totalSteps: computed(() => renderableSteps.value.length),
  currentStepIndex: computed(() => currentRenderableIndex.value),
})

onMounted(() => loadFlow())
</script>
